/**
 * Zone Details View Component
 *
 * Displays detailed assignments for a specific zone
 */

import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSyncedStore } from '@syncedstore/react'
import { todoStore } from 'src/store/todo'
import type { TaskType, Worker, Zone, Location } from 'src/store/todo/types'
import { getZoneAssignments, getWorkerAllHarvestsSummary } from '../../utils/filtering'
import PageHeader from 'src/components/shared/page-header'
import Container from 'src/components/shared/container'
import Fallback from 'src/components/shared/fallback'
import AssignmentCard from './assignment-card'
import WorkerValueSummaryModal from 'src/components/shared/worker-value-summary-modal'
import type { RecordModalState, DoneModalState } from '../../types'

type ZoneDetailsViewProps = {
  /** Currently selected team ID */
  selectedTeamId: string
  /** Callback to open record modal */
  onOpenRecordModal: (state: RecordModalState) => void
  /** Callback to open done modal */
  onOpenDoneModal: (state: DoneModalState) => void
  /** Callback to set delete confirm ID */
  onSetDeleteConfirm: (logId: string) => void
}

/**
 * ZoneDetailsView component showing all assignments for a zone
 *
 * Shows ALL worker assignments for the specified zone, regardless of which worker was clicked.
 * This allows users to see complete zone activity when navigating from any worker badge.
 *
 * @example
 * // Navigate to: /analytics/zone/:zoneId
 * <ZoneDetailsView
 *   selectedTeamId={teamId}
 *   onOpenRecordModal={handleRecordModal}
 *   onOpenDoneModal={handleDoneModal}
 *   onSetDeleteConfirm={handleDeleteConfirm}
 * />
 */
const ZoneDetailsView: React.FC<ZoneDetailsViewProps> = ({
  selectedTeamId,
  onOpenRecordModal,
  onOpenDoneModal,
  onSetDeleteConfirm,
}) => {
  const { zoneId } = useParams<{ zoneId: string }>()
  const navigate = useNavigate()
  const state = useSyncedStore(todoStore)

  // State for all harvests summary modal
  const [summaryModalState, setSummaryModalState] = React.useState<{
    isOpen: boolean
    workerName: string
    workerId: string
    harvestTaskIds: string[]
  } | null>(null)

  // Get data from synced store
  const zones = state.zones?.filter((z: any) => z.isActive) || []
  const zone = zones.find((z: Zone) => z.id === zoneId)
  const workers = state.workers || []
  const tasks = (state.taskTypes || []) as TaskType[]
  const locations = (state.locations || []) as Location[]

  // Get ALL zones for summary modal (not filtered)
  const allZones = (state.zones || []) as Zone[]

  // Get all harvest task IDs
  const harvestTaskIds = React.useMemo(
    () => tasks.filter((t) => t.taskType.toUpperCase().includes('HVST')).map((t) => t.id),
    [tasks]
  )

  // Build maps for quick lookup
  const workersMap = React.useMemo(() => {
    const map: Record<string, Worker> = {}
    workers.forEach((w: any) => {
      map[w.id] = w
    })
    return map
  }, [workers])

  const tasksMap = React.useMemo(() => {
    const map: Record<string, TaskType> = {}
    tasks.forEach((t) => {
      map[t.id] = t
    })
    return map
  }, [tasks])

  // Get ALL zone assignments (no filtering by worker)
  const zoneAssignments = getZoneAssignments(
    state.workerAssignments,
    zoneId || '',
    selectedTeamId
  )

  const handleOpenRecordModalWrapper = (
    workerId: string,
    taskTypeId: string,
    zoneId: string,
    locationId?: string
  ) => {
    onOpenRecordModal({
      isOpen: true,
      assignmentId: `${workerId}-${taskTypeId}-${zoneId}-${locationId || ''}`,
      workerId,
      taskTypeId,
      zoneId,
      locationId,
    })
  }

  const handleOpenDoneModalWrapper = (
    assignmentId: string,
    workerId: string,
    workerName: string,
    taskTypeId: string,
    taskName: string,
    zoneId: string,
    zoneName: string
  ) => {
    onOpenDoneModal({
      isOpen: true,
      assignmentId,
      workerId,
      workerName,
      taskTypeId,
      taskName,
      zoneId,
      zoneName,
    })
  }

  const handleOpenSummaryModalWrapper = (
    workerId: string,
    workerName: string,
    harvestTaskIds: string[]
  ) => {
    console.log('Opening summary modal:', { workerId, workerName, harvestTaskIds })
    console.log('allZones:', allZones)
    console.log('locations:', locations)
    setSummaryModalState({
      isOpen: true,
      workerName,
      workerId,
      harvestTaskIds,
    })
  }

  return (
    <div className="min-h-screen w-full flex flex-col bg-gray-50">
      <PageHeader
        title={`${zone?.zoneMoniker || 'Zone'} - Assignments`}
        isDetail
        onBack={() => navigate('/analytics')}
      />

      <div className="flex-1 overflow-y-auto">
        <Container>
          <div className="flex flex-col gap-4 py-6 pb-32">
            {zoneAssignments.length > 0 ? (
              <div className="space-y-3">
                {zoneAssignments.map((assignment) => {
                  const worker = workersMap[assignment.workerId]
                  const task = tasksMap[assignment.taskTypeId]

                  if (!worker || !task) return null

                  return (
                    <AssignmentCard
                      key={assignment.id}
                      assignment={assignment}
                      worker={worker}
                      task={task}
                      zone={zone!}
                      tasks={tasks}
                      tasksMap={tasksMap}
                      logs={state.workerAssignmentLogs}
                      locations={locations}
                      selectedTeamId={selectedTeamId}
                      harvestTaskIds={harvestTaskIds}
                      onOpenRecordModal={handleOpenRecordModalWrapper}
                      onOpenDoneModal={handleOpenDoneModalWrapper}
                      onOpenSummaryModal={handleOpenSummaryModalWrapper}
                      onSetDeleteConfirm={onSetDeleteConfirm}
                      state={state}
                    />
                  )
                })}
              </div>
            ) : (
              <Fallback title="No assignments found for this zone" />
            )}
          </div>
        </Container>
      </div>

      {/* All Harvests Summary Modal */}
      {summaryModalState && (() => {
        console.log('Rendering modal with state:', summaryModalState)
        const summaryData = getWorkerAllHarvestsSummary(
          state.workerAssignmentLogs,
          allZones,
          locations,
          summaryModalState.workerId,
          summaryModalState.harvestTaskIds
        )
        console.log('Summary data:', summaryData)
        return (
          <WorkerValueSummaryModal
            isOpen={summaryModalState.isOpen}
            workerName={summaryModalState.workerName}
            taskName="All Harvests"
            summaryData={summaryData}
            onClose={() => setSummaryModalState(null)}
          />
        )
      })()}
    </div>
  )
}

export default ZoneDetailsView
